import type { AwarenessState } from '~/store/entitlements/useTrial';

import Backdrop from '../Backdrop/view';
import Button from '../../atoms/Button/view';
import { Icon } from '../../atoms/Icon/view';
import { Text } from '../../atoms/Text/view';

interface PropsTrialUpgradeSheet {
  open: boolean;
  state: AwarenessState;
  daysRemaining: number;
  onClose: () => void;
  onUpgrade: () => void;
}

const BENEFITS = [
  'Contratos e parcelas sem limites',
  'Relatórios completos e análise de risco por contrato',
  'Cotações de mercado e previsão do tempo',
];

interface Copy {
  badge: string;
  badgeIcon: 'icon-gift' | 'icon-clock' | 'icon-lock';
  title: string;
  body: string;
  cta: string;
}

const COPY: Record<AwarenessState, Copy> = {
  trial_active: {
    badge: 'Período gratuito',
    badgeIcon: 'icon-gift',
    title: 'Você está com acesso completo',
    body: 'Continue acompanhando seus contratos, relatórios e recursos premium do Paga Agro sem limitações enquanto seu período gratuito está ativo.',
    cta: 'Conhecer plano premium',
  },
  trial_ending: {
    badge: 'Período acabando',
    badgeIcon: 'icon-clock',
    title: 'Seu período gratuito está acabando',
    body: 'Assine agora para não perder o acesso aos seus contratos, relatórios completos e cotações de mercado.',
    cta: 'Assinar agora',
  },
  trial_ended: {
    badge: 'Período encerrado',
    badgeIcon: 'icon-clock',
    title: 'Seu período gratuito terminou',
    body: 'Escolha um plano premium para retomar o acesso completo. Seus dados continuam salvos, é só assinar.',
    cta: 'Ver planos',
  },
  cancelled: {
    badge: 'Plano cancelado',
    badgeIcon: 'icon-lock',
    title: 'Seu plano foi cancelado',
    body: 'Reative quando quiser para voltar a ter contratos sem limites, relatórios completos e cotações de mercado.',
    cta: 'Reativar plano premium',
  },
  expired: {
    badge: 'Plano expirado',
    badgeIcon: 'icon-clock',
    title: 'Seu plano expirou',
    body: 'Regularize o pagamento para retomar de onde parou. Seus dados continuam salvos e seguros.',
    cta: 'Reativar plano premium',
  },
  no_plan: {
    badge: 'Premium',
    badgeIcon: 'icon-gift',
    title: 'Desbloqueie o Paga Agro premium',
    body: 'Contratos e parcelas sem limites, relatórios completos e cotações de mercado em um só lugar.',
    cta: 'Conhecer os planos',
  },
};

function countdownLabel(state: AwarenessState, days: number): string | null {
  if (state !== 'trial_active' && state !== 'trial_ending') return null;
  if (days <= 0) return 'Último dia de acesso gratuito';
  if (days === 1) return 'Falta 1 dia de acesso gratuito';
  return `Faltam ${days} dias de acesso gratuito`;
}

export default function TrialUpgradeSheet({
  open,
  state,
  daysRemaining,
  onClose,
  onUpgrade,
}: PropsTrialUpgradeSheet) {
  const copy = COPY[state];
  const countdown = countdownLabel(state, daysRemaining);

  return (
    <Backdrop open={open} onClose={onClose} className="overflow-hidden">
      <div className="flex h-[6px]">
        <div className="w-1/2 bg-primary-01" />
        <div className="w-1/4 bg-secondary-01" />
        <div className="w-1/4 bg-support-01" />
      </div>

      <div className="relative p-medium">
        <button
          type="button"
          aria-label="Fechar"
          onClick={onClose}
          className="absolute right-medium top-medium flex h-[32px] w-[32px] items-center justify-center rounded-full bg-intense-02 transition-colors hover:bg-intense-03 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-01/30"
        >
          <Icon name="icon-close" size={14} color="#606060" rounded={false} />
        </button>

        <span className="inline-flex items-center gap-quark rounded-full bg-support-01 px-nano py-quark text-[10px] font-bold uppercase tracking-[0.12em] text-primary-01">
          <Icon name={copy.badgeIcon} size={12} color="#1D4733" rounded={false} />
          {copy.badge}
        </span>

        <Text as="h2" className="mt-smaller text-headingH1 text-complement-01">
          {copy.title}
        </Text>
        <Text className="mt-quark text-paragraphSM text-intense-06">{copy.body}</Text>

        {countdown ? (
          <div className="mt-medium flex items-center gap-nano rounded-small border border-support-01 bg-intense-02 px-small py-smaller">
            <Icon name="icon-clock" size={16} color="#3F7D5F" rounded={false} />
            <Text className="text-bodyMD text-primary-01">{countdown}</Text>
          </div>
        ) : null}

        <ul className="mt-medium flex flex-col gap-smaller">
          {BENEFITS.map((benefit) => (
            <li key={benefit} className="flex items-center gap-nano">
              <span className="flex h-[20px] w-[20px] shrink-0 items-center justify-center rounded-full bg-support-01">
                <Icon name="icon-check" size={11} color="#1D4733" rounded={false} />
              </span>
              <Text className="text-bodySM text-intense-07">{benefit}</Text>
            </li>
          ))}
        </ul>

        <Button
          text={copy.cta}
          rightIcon="icon-chevron-right"
          fullWidth
          className="mt-large"
          onClick={onUpgrade}
        />
        <button
          type="button"
          onClick={onClose}
          className="mt-smaller w-full rounded-small py-smaller text-bodyMD text-intense-05 transition-colors hover:bg-intense-02 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-01/20"
        >
          Agora não
        </button>
      </div>
    </Backdrop>
  );
}
